import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { estados, cidadesPorEstado } from '@/utils/locations';
import { Film } from 'lucide-react';
import SocialLoginButtons from '@/components/auth/SocialLoginButtons';
import { getGoogleWebClientId, getAppleWebClientId } from '@/config/runtimeConfig';
import PageSeo from '@/components/seo/PageSeo';

const PENDING_REFERRAL_KEY = 'pending_referral_code';

const Cadastro = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome: '',
    telefone: '',
    role: '',
    cidade: '',
    estado: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cidadesDisponiveis, setCidadesDisponiveis] = useState([]);
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    try {
      const fromUrl = searchParams.get('ref')?.trim().toLowerCase();
      const fromStorage = localStorage.getItem(PENDING_REFERRAL_KEY)?.trim().toLowerCase();
      const code = fromUrl || fromStorage || '';
      if (code) {
        setReferralCode(code);
        localStorage.setItem(PENDING_REFERRAL_KEY, code);
      }
    } catch {
      /* ignore */
    }
  }, [searchParams]);

  useEffect(() => {
    const roleParam = searchParams.get('role')?.trim().toLowerCase();
    if (roleParam === 'videomaker' || roleParam === 'client') {
      setFormData((prev) => ({ ...prev, role: roleParam }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRoleChange = (value) => {
    setFormData({
      ...formData,
      role: value
    });
  };

  const handleEstadoChange = (value) => {
    setFormData({
      ...formData,
      estado: value,
      cidade: '' // Limpar cidade ao mudar estado
    });
    setCidadesDisponiveis(cidadesPorEstado[value] || []);
  };

  const handleCidadeChange = (value) => {
    setFormData({
      ...formData,
      cidade: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.role) {
      setError('Selecione se você quer se cadastrar como Cliente ou Videomaker.');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const payload = { ...formData };
      if (formData.role === 'videomaker' && referralCode) {
        payload.referral_code = referralCode;
      }
      const result = await signup(payload);
      if (formData.role === 'videomaker' && referralCode) {
        try {
          localStorage.removeItem(PENDING_REFERRAL_KEY);
        } catch {
          /* ignore */
        }
      }
      if (result.pendingVerification) {
        navigate('/login', {
          state: {
            verifyEmailHint: true,
            email: formData.email,
            verifyMessage: result.message,
          },
        });
        return;
      }
      navigate('/completar-perfil');
    } catch (err) {
      setError(err.message || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center py-12 px-4">
      <PageSeo
        title="Criar conta | Ufokko"
        description="Cadastre-se na Ufokko como cliente ou videomaker. Gratuito — publique jobs de vídeo ou receba trabalhos na sua região."
        path="/cadastro"
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-2xl"
      >
        <Card className="shadow-2xl border-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
              <Film className="w-8 h-8 text-primary" aria-hidden />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#0E76FF] to-[#2563EB] bg-clip-text text-transparent">
              Criar Conta
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              Cadastre-se como cliente ou videomaker
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(getGoogleWebClientId() || getAppleWebClientId()) ? (
              <>
                <SocialLoginButtons disabled={loading} />
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 my-4">
                  ou preencha o formulário abaixo
                </p>
              </>
            ) : null}
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <AlertDescription className="text-red-800 dark:text-red-200">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {referralCode && (
                <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                  <AlertDescription className="text-blue-900 dark:text-blue-100 text-sm">
                    Convite de indicação ativo ({referralCode}). Para vincular ao indicador, escolha{' '}
                    <strong>Videomaker</strong> ao criar a conta.
                  </AlertDescription>
                </Alert>
              )}

              {/* Tipo de Usuário */}
              <div className="space-y-3">
                <Label className="text-gray-700 dark:text-gray-300">Quero me cadastrar como:</Label>
                <RadioGroup
                  value={formData.role || undefined}
                  onValueChange={handleRoleChange}
                  className="grid grid-cols-2 gap-4"
                >
                  <div>
                    <RadioGroupItem
                      value="client"
                      id="client"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="client"
                      className="flex flex-col items-center justify-between rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-4 hover:bg-gray-50 dark:hover:bg-gray-600 peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-900/20 cursor-pointer transition-all"
                    >
                      <span className="text-3xl mb-2">👤</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">Cliente</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Contratar videomakers
                      </span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem
                      value="videomaker"
                      id="videomaker"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="videomaker"
                      className="flex flex-col items-center justify-between rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-4 hover:bg-gray-50 dark:hover:bg-gray-600 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-primary/20 cursor-pointer transition-all"
                    >
                      <span className="text-3xl mb-2">🎥</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">Videomaker</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Oferecer serviços
                      </span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Dados Pessoais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome" className="text-gray-700 dark:text-gray-300">
                    Nome Completo *
                  </Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    placeholder="Seu nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone" className="text-gray-700 dark:text-gray-300">
                    Telefone *
                  </Label>
                  <Input
                    id="telefone"
                    name="telefone"
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={formData.telefone}
                    onChange={handleChange}
                    required
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 dark:text-gray-300">
                  Senha *
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                />
              </div>

              {/* Localização */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estado" className="text-gray-700 dark:text-gray-300">
                    Estado *
                  </Label>
                  <Select value={formData.estado} onValueChange={handleEstadoChange} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {estados.map((estado) => (
                        <SelectItem key={estado.sigla} value={estado.sigla}>
                          {estado.nome} ({estado.sigla})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cidade" className="text-gray-700 dark:text-gray-300">
                    Cidade *
                  </Label>
                  <Select 
                    value={formData.cidade} 
                    onValueChange={handleCidadeChange} 
                    disabled={!formData.estado}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.estado ? "Selecione a cidade" : "Selecione o estado primeiro"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cidadesDisponiveis.length > 0 ? (
                        cidadesDisponiveis.map((cidade) => (
                          <SelectItem key={cidade} value={cidade}>
                            {cidade}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="outro" disabled>
                          Nenhuma cidade disponível
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#0E76FF] to-[#2563EB] hover:from-[#0D6AE6] hover:to-[#1d4ed8] text-white font-semibold py-6 shadow-lg hover:shadow-xl transition-all"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Criando conta...
                  </div>
                ) : (
                  'Criar Conta'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Já tem uma conta?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                >
                  Faça login
                </Link>
              </p>
              <Link
                to="/"
                className="text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors block"
              >
                ← Voltar para Home
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Info Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400"
        >
          <p>🔒 Seus dados estão seguros conosco</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Cadastro;
